/**
 * The then/now comparison in a story chapter.
 *
 * Two images of the same view at the same camera position: the documentary photograph of
 * the site as it stands, and a reconstruction of it conditioned on that photograph. A
 * wipe between them is the only presentation that makes the pairing legible — side by
 * side, the eye cannot hold two near-identical frames well enough to see what changed.
 *
 * The control is a real `input[type=range]`, not a synthesised handle. That gets keyboard
 * operation, screen-reader semantics and touch behaviour from the platform rather than
 * from code that would have to reimplement all three and would get some of it wrong.
 *
 * The reconstruction always carries its label. These are real places under UNESCO
 * inscription, and an unlabelled interpretation sitting next to a photograph is a claim
 * the project cannot support.
 */

import type { UiCopy } from "./i18n";

// Eager so the chapter can look a slug up synchronously while building its DOM. Vite
// fingerprints each file, which is what earns them the immutable cache header.
const RECONSTRUCTIONS = import.meta.glob<string>("./ornament/*-then.webp", {
  eager: true,
  query: "?url",
  import: "default",
});

/** The reconstruction for a slug, or null when that exhibition has none. */
export function reconstructionUrl(slug: string): string | null {
  return RECONSTRUCTIONS[`./ornament/${slug}-then.webp`] ?? null;
}

export function createReveal(
  photographUrl: string,
  copy: UiCopy,
  reconstruction: string,
): HTMLElement {
  const figure = document.createElement("figure");
  figure.className = "reveal";
  figure.dataset.reveal = "";

  const frame = document.createElement("div");
  frame.className = "reveal__frame";

  // The photograph sits underneath and is never clipped: whatever the slider does, the
  // evidence stays on screen.
  const now = document.createElement("img");
  now.className = "reveal__layer reveal__layer--now";
  now.src = photographUrl;
  now.alt = copy.revealPhotographAlt;
  now.width = 1600;
  now.height = 1067;
  now.loading = "lazy";

  const then = document.createElement("img");
  then.className = "reveal__layer reveal__layer--then";
  then.src = reconstruction;
  then.alt = copy.revealReconstructionAlt;
  then.width = 1600;
  then.height = 1067;
  then.loading = "lazy";

  const thenBadge = badge(copy.revealThen, "reveal__badge--then");
  const nowBadge = badge(copy.revealNow, "reveal__badge--now");

  const slider = document.createElement("input");
  slider.type = "range";
  slider.className = "reveal__slider";
  slider.min = "0";
  slider.max = "100";
  slider.value = "50";
  slider.step = "1";
  slider.setAttribute("aria-label", copy.revealSliderLabel);
  // Percentages read as "50%" without this; the useful announcement is which view wins.
  slider.setAttribute("aria-valuetext", copy.revealValueText(50));

  const apply = (): void => {
    const position = Number(slider.value);
    figure.style.setProperty("--reveal-position", `${position}%`);
    slider.setAttribute("aria-valuetext", copy.revealValueText(position));
  };
  slider.addEventListener("input", apply);
  apply();

  const handle = document.createElement("span");
  handle.className = "reveal__handle";
  handle.setAttribute("aria-hidden", "true");

  frame.append(now, then, thenBadge, nowBadge, handle, slider);

  const caption = document.createElement("figcaption");
  caption.className = "reveal__caption";
  caption.textContent = copy.revealCaption;

  figure.append(frame, caption);
  return figure;
}

function badge(text: string, modifier: string): HTMLElement {
  const element = document.createElement("span");
  element.className = `reveal__badge ${modifier}`;
  element.textContent = text;
  return element;
}
